import React, {useContext, useState, useEffect, useMemo} from 'react'
import { CampaignContext } from "./campaign-context";
import { Modal, useModalHide } from '../components/Modal';
import { SavingButton } from '../components/SavingButton';
import { Campaign, TaskStatus, pauseCampaign, prepareCampaign, scheduleCampaign, startCampaign, statusCampaign, CampaignStatus, progressStatusCampaign } from '../campaign-api';
import { ProgressBar } from '../components/ProgressBar';
import { MessageStatusChart } from './campaign-chart';
import { useAlert, useConfirm, useShowDialog } from '../components/Dialogs';
import { Loading } from '../components/Loading';
import { Alert } from '../components/Alert';
import { DatePicker } from 'datepicker';
import { formatDatetime } from 'simple-utils/dist/date-utils';
import { sleep } from 'simple-utils';
import { formatDuration, useETA } from '../hooks/eta-hook';
import { useErrorAlert } from '../components/use-animation';
import { useBotEvents } from '../bot-loaders';


function StartTask(){
    const {state:{viewCampaign, bot}, dispatch} = useContext(CampaignContext);
    const [saving, setSaving] = useState(false);
    const [scheduleMode, setScheduleMode] = useState(viewCampaign.scheduled != null)
    const hide = useModalHide();

    async function doStart(){
        if(saving) return;
        if(!['none', 'paused'].includes(viewCampaign.status)) return;
        try {
            setSaving(true);
            await prepareCampaign(bot, viewCampaign);
            await startCampaign(bot, viewCampaign);
            const campaign:Campaign = {...viewCampaign, status: "running"}
            dispatch({type:"setViewCampaign", campaign});
        }catch(e){
            console.log(e);
        }
        setSaving(false);
    }

    if(scheduleMode)
        return <ScheduleForm onCancel={() => setScheduleMode(false)} />

    return <>
        <h4>{viewCampaign.name}</h4>

        {viewCampaign.status == 'paused' && <div className='my-3'>
                <Alert content="A campanha está pausada" className='alert-warning mb-3' />
                <MessageStatusChart />
                <hr />
            </div>}

        <p>Deseja iniciar o envio das mensagens para o público <b>{viewCampaign.audienceName}</b>?</p>
        <div className='mt-2 text-md-end'>
            <button className='btn btn-outline-primary mt-2 me-2' onClick={hide}>Cancelar</button>
            <button className='btn btn-primary mt-2 me-2' onClick={() => setScheduleMode(true)}><i className='fa fa-clock me-1'></i> Agendar Envio</button>
            <SavingButton saving={saving} icon='fab fa-whatsapp' className='btn btn-danger mt-2' onClick={doStart}>
                {viewCampaign.status == 'none' ? 'Enviar Mensagens' : 'Continuar Envio'}
            </SavingButton>
        </div>
    </>
}

function ScheduleForm({onCancel}:{onCancel:()=>void}){
    const {state:{viewCampaign, bot}, dispatch} = useContext(CampaignContext);
    const [saving, setSaving] = useState(false);
    const [date, setDate] = useState(viewCampaign.scheduled ?  formatDatetime(viewCampaign.scheduled) : "");
    
    async function setupSchedule(){
        setSaving(true)
        await sleep(1500);
        try {
            var current = viewCampaign.scheduled ?  formatDatetime(viewCampaign.scheduled) : "";
            if(date != current){
                let scheduled = await scheduleCampaign(bot, viewCampaign, date);
                dispatch({type: "setViewCampaign", campaign: {...viewCampaign, scheduled, status: scheduled ? 'scheduled' : 'none'}})
            }
        }catch(e){
        }
        setSaving(false);
    }

    return <>
        <h4>{viewCampaign.name}</h4>

        <p>Escolha um horário para fazer o envio das mensagens.</p>

        <div className="d-md-flex align-items-center">
            <div className="me-0 me-md-3">
                <label className="col-form-label">Data de envio: </label>
            </div>
            <DatePicker date={date} onChange={(e) => setDate(e)} isTimePicker />
        </div>

        <div className="mt-3 text-md-end">
            <button className='btn btn-outline-primary mt-2 me-2' onClick={onCancel}>Cancelar</button>
            <SavingButton saving={saving} className='btn btn-primary mt-2 me-2' icon='far fa-clock' onClick={() => setupSchedule()}>Agendar</SavingButton>
        </div>
    </>
}

function ScheduleState(){
    const {state: {viewCampaign, bot}, dispatch} = useContext(CampaignContext);
    const [scheduleMode, setScheduleMode] = useState(false)
    const [saving, setSaving] = useState(false);
    const hide = useModalHide();

    if(scheduleMode){
        return <ScheduleForm onCancel={() => setScheduleMode(false)} />
    }

    async function cancel(){
        setSaving(true)
        await sleep(1500);
        try {
            let scheduled = await scheduleCampaign(bot, viewCampaign, "");
            dispatch({type: "setViewCampaign", campaign: {...viewCampaign, scheduled, status: scheduled ? 'scheduled' : 'none'}})
        }catch(e){
        }
        setSaving(false);
    }

    return <>
        <h4>{viewCampaign.name}</h4>
        <p>Esta campanha está agendada para <b>{formatDatetime(viewCampaign.scheduled)}</b></p>
        <p><SavingButton onClick={cancel} icon='fa fa-ban' saving={saving} className='btn btn-sm btn-secondary'>Cancelar Agendamento</SavingButton></p>

        <div className="mt-3 text-md-end">
            <button className='btn btn-outline-primary mt-2 me-2' onClick={hide}>Cancelar</button>
            <SavingButton className='btn btn-primary mt-2 me-2' icon='far fa-clock' onClick={() => setScheduleMode(true)}> Reagendar</SavingButton>
        </div>
    </>
}

function StatusTask(){
    const {state:{viewCampaign, bot}, dispatch} = useContext(CampaignContext);
    const [status, setStatus] = useState<TaskStatus>(null);
    const [error, setError] = useState<string>(null);
    const [token, setToken] = useState<string>(null);

    useEffect(()=>{
        (async function(){
            try{
                const s = await progressStatusCampaign(bot, viewCampaign);
                setStatus(s.taskStatus);

                if(s.status != viewCampaign.status)
                    dispatch({type: 'setViewCampaign', campaign: {...viewCampaign, status: s.status}});
                setToken(s.eventToken);
            }catch(e){
                setError("Erro");
            }
        })()
    }, []);

    useEffect(() => {
        function onCampaingProgress(s:{taskStatus: TaskStatus, status: CampaignStatus}){
            setStatus(s.taskStatus);

            if(s.status != viewCampaign.status)
                dispatch({type: 'setViewCampaign', campaign: {...viewCampaign, status: s.status}});
        }

        if(token == null) return;
        return useBotEvents(token, "campaign-status", onCampaingProgress, () => setError("Erro ao ler status"));

    }, [token]);

    const {estimatedTime} = useETA( status ? {currentItem: status.done + status.error, totalItems: status.total} : {currentItem: 0, totalItems: 0})

    if(error) return <Alert content="Erro ao comunicar com o servidor" />

    if(!status) return <>Loading...</>

    return <> 
        <h4 className='mb-3'>{viewCampaign.name}</h4>
        <p>Status {status.done + status.error} de {status.total} {status.complete && <i className="ms-2 fa fa-check text-success zoom-appear"></i>} <br />
        Envios: {status.done}<br />
        Erros: {status.error}</p>

        { !status.complete && <p>
            Previsão de término: <b>{formatDuration(estimatedTime / 1000)}</b>
        </p> }

        <ProgressBar done={status.done + status.error} total={status.total} />

        {viewCampaign.status == 'running' && <PauseBar status={status} />}
    </>
}

function PauseBar({status}:{status:TaskStatus}){
    const {state:{viewCampaign, bot, campaigns}, dispatch} = useContext(CampaignContext);
    const [pausing, setPausing] = useState(false)
    const confirm = useConfirm("Tem certeza que deseja pausar a campanha?", {})
    const hide = useModalHide();

    useEffect(() =>{    
        if(!pausing) return
        if(!status.complete) return
        (async function(){
            await hide(); 
            dispatch({type:"setViewCampaign", campaign: {...viewCampaign, status: "paused"}})
        })()
        
    }, [status, pausing])

    async function pause(){
        if(!(await confirm()))
            return
        setPausing(true);
        await pauseCampaign(bot, viewCampaign);
    }

    if(pausing) 
        return <>
            <Loading className='mt-3' label='Pausando o envio...' />
        </>

    return <>
        <hr />
        <button className='btn btn-warning' onClick={pause}>Pausar o envio</button>
    </>
}


function SendMessageModal(){
    const {state:{viewCampaign}} = useContext(CampaignContext);

    if(viewCampaign.status == 'done')
        return <MessageStatusChart />

    if(viewCampaign.status == 'none' || viewCampaign.status == 'paused')
        return <StartTask />

    if(viewCampaign.status == 'scheduled'){
        return <ScheduleState />
    }

    return <StatusTask />    
}


export function SendMessageModalButton(){
    const {state:{viewCampaign}} = useContext(CampaignContext);
    const [show, setShow] = useState(false);
    const noMessageAlert = useAlert("Antes, ajuste a mensgem da campanha.", {})

    function showModal(){
        try {
            if(!viewCampaign.media){
                if(!viewCampaign.template) 
                    throw "Ajuste a mensagem antes.";
            }
            setShow(true);
        }catch(e){
            noMessageAlert();
        }
    }

    return <>
        <button className='btn btn-primary' onClick={showModal}>{['none', 'paused'].includes(viewCampaign.status)  ? 'Enviar Mensagens' : 'Status de Envio'}</button>
        {show && <Modal onHide={() => setShow(false)} title="Envio de Mensagens">
            <SendMessageModal />
        </Modal>}
    </>

}